/**
 * MuiThemeBridge — makes MUI follow the app's light/dark mode.
 *
 * The MUI theme used to be built once at module scope in index.js with nothing
 * but a font and a primary colour:
 *
 *     createTheme({ typography: {...}, palette: { primary: { main: PRIMARY_HEX } } })
 *
 * No `palette.mode`, so MUI defaulted to LIGHT and stayed there for the life of
 * the tab — a static object created before render cannot react to `data-theme`
 * flipping on <html>. Every MUI surface in the app was therefore pinned light:
 * white Dialog and Menu paper, `rgba(0,0,0,0.87)` body text, black IconButtons,
 * on top of a #0a0a0b page. Building the theme inside the tree, keyed on the
 * resolved theme from ThemeContext, is what makes it flip.
 *
 * Why hex and not `var(--token)`: MUI does real colour maths on palette values
 * — `alpha()`, `lighten()`, `darken()`, and `augmentColor` deriving light/dark
 * variants — by PARSING the string. A CSS custom property is opaque to that and
 * throws on decomposition. So this reads the same `palette` object the CSS vars
 * are generated from and picks the mode's branch explicitly, which is exactly
 * what the flat accessors in trodoColors.js are documented for.
 */
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import React, { useMemo } from 'react';
import { useTheme } from 'shared/theme/ThemeContext';
import {
  palette as tokens,
  PRIMARY_HEX,
  primaryHover,
} from 'shared/theme/trodoColors';

/**
 * Build a MUI theme for one mode.
 *
 * Exported for tests — `buildMuiTheme('dark').palette.background.paper` should
 * be the dark surface token, not MUI's default #121212.
 *
 * @param {'light'|'dark'} mode
 */
export function buildMuiTheme(mode) {
  const pick = (token) => token[mode];

  return createTheme({
    typography: {
      fontFamily: "'Inter', sans-serif",
    },
    // The rest of the app collapses every radius to 6px (see tailwind.config.js);
    // MUI's default 4px made Dialogs and Menus visibly sharper than the panels
    // they open over.
    shape: {
      borderRadius: 6,
    },
    palette: {
      mode,
      primary: {
        main: PRIMARY_HEX,
        dark: primaryHover,
        contrastText: pick(tokens.text.onBrand),
      },
      // `paper` is what Dialog, Menu and Popover render on — the token reserved
      // for exactly those ("popovers, dropdowns, modals").
      background: {
        default: pick(tokens.surface.base),
        paper: pick(tokens.surface.raised),
      },
      text: {
        primary: pick(tokens.text.primary),
        secondary: pick(tokens.text.secondary),
        disabled: pick(tokens.text.disabled),
      },
      divider: pick(tokens.border.default),
      error: { main: pick(tokens.feedback.error) },
      warning: { main: pick(tokens.feedback.warning) },
      success: { main: pick(tokens.feedback.success) },
      info: { main: pick(tokens.feedback.info) },
      // IconButton colour and row hover/selection come from here. Left at MUI's
      // defaults these are black-on-black in dark mode.
      action: {
        active: pick(tokens.text.secondary),
        hover: pick(tokens.state.hoverBg),
        selected: pick(tokens.state.selectedBg),
        disabled: pick(tokens.text.disabled),
        disabledBackground: pick(tokens.state.hoverBg),
      },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            // MUI's dark mode paints an "elevation overlay" onto Paper as a
            // white linear-gradient background-IMAGE, on top of whatever
            // background-color the palette gives it. Left on, every Dialog and
            // Menu would render lighter than --surface-raised and drift further
            // apart the higher its elevation — so the token would be set
            // correctly and still not be what you see.
            backgroundImage: 'none',
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            // MUI hardcodes rgba(0,0,0,0.5). The token deepens for dark mode
            // (0.45 -> 0.65), which is where the extra separation is needed.
            backgroundColor: pick(tokens.surface.overlay),
          },
        },
      },
    },
  });
}

/**
 * Provides the MUI theme for the current mode. Must render INSIDE
 * `ThemeProvider` from shared/theme/ThemeContext, which is what it reads.
 */
export default function MuiThemeBridge({ children }) {
  const { theme } = useTheme();
  // Rebuilt only when the resolved theme actually changes — createTheme is not
  // cheap enough to run on every render of the app root.
  const muiTheme = useMemo(() => buildMuiTheme(theme === 'dark' ? 'dark' : 'light'), [theme]);

  return <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>;
}
